/**
 * User Preferences Service
 *
 * Syncs user preferences (userMode, showBTCComparison, etc.) with Supabase
 * for authenticated users. Falls back to localStorage for unauthenticated users.
 */

import { supabase } from '../lib/supabase/client';
import { StorageService } from './storage';

export interface DisplayPreferences {
  userMode: 'simple' | 'expert';
  showBTCComparison: boolean;
  // Add more preferences as needed
}

const DEFAULT_PREFERENCES: DisplayPreferences = {
  userMode: 'simple',
  showBTCComparison: false,
};

// Local storage keys
const LOCAL_KEYS = {
  userMode: 'user_mode',
  showBTCComparison: 'show_btc_comparison',
};

class UserPreferencesService {
  private cache: DisplayPreferences | null = null;
  private userId: string | null = null;
  private syncInProgress = false;

  /**
   * Initialize the service with user ID
   */
  setUserId(userId: string | null) {
    if (this.userId !== userId) {
      this.userId = userId;
      this.cache = null; // Reset cache when user changes
    }
  }

  /**
   * Get all display preferences
   */
  async getPreferences(): Promise<DisplayPreferences> {
    // Return cache if available
    if (this.cache) {
      return this.cache;
    }

    // If user is logged in, fetch from database
    if (this.userId) {
      try {
        const { data, error } = await supabase
          .from('user_preferences')
          .select('display_preferences')
          .eq('user_id', this.userId)
          .single();

        if (!error && data?.display_preferences) {
          const dbPrefs = data.display_preferences as Partial<DisplayPreferences>;
          this.cache = {
            ...DEFAULT_PREFERENCES,
            ...dbPrefs,
          };

          // Also update localStorage for offline access
          this.syncToLocalStorage(this.cache);

          return this.cache;
        }
      } catch (err) {
        console.warn('[UserPreferences] Error fetching from DB, using localStorage:', err);
      }
    }

    // Fallback to localStorage
    return this.getFromLocalStorage();
  }

  /**
   * Get a single preference value
   */
  async getPreference<K extends keyof DisplayPreferences>(key: K): Promise<DisplayPreferences[K]> {
    const prefs = await this.getPreferences();
    return prefs[key];
  }

  /**
   * Update a single preference
   */
  async setPreference<K extends keyof DisplayPreferences>(
    key: K,
    value: DisplayPreferences[K]
  ): Promise<boolean> {
    // Update cache
    if (!this.cache) {
      this.cache = await this.getPreferences();
    }
    this.cache[key] = value;

    // Always update localStorage for immediate access
    const localKey = LOCAL_KEYS[key];
    if (localKey) {
      StorageService.set(localKey, value);
    }

    // If user is logged in, sync to database
    if (this.userId) {
      return this.syncToDatabase();
    }

    return true;
  }

  /**
   * Update multiple preferences at once
   */
  async setPreferences(prefs: Partial<DisplayPreferences>): Promise<boolean> {
    // Update cache
    if (!this.cache) {
      this.cache = await this.getPreferences();
    }
    this.cache = { ...this.cache, ...prefs };

    // Update localStorage
    this.syncToLocalStorage(this.cache);

    // If user is logged in, sync to database
    if (this.userId) {
      return this.syncToDatabase();
    }

    return true;
  }

  /**
   * Sync preferences from localStorage to database after login
   */
  async syncAfterLogin(userId: string): Promise<void> {
    this.setUserId(userId);

    try {
      // First, check if user has existing preferences in DB
      const { data: existing } = await supabase
        .from('user_preferences')
        .select('display_preferences')
        .eq('user_id', userId)
        .single();

      if (existing?.display_preferences) {
        // DB preferences exist - use them and update localStorage
        const dbPrefs = existing.display_preferences as Partial<DisplayPreferences>;
        this.cache = { ...DEFAULT_PREFERENCES, ...dbPrefs };
        this.syncToLocalStorage(this.cache);
        console.log('[UserPreferences] Loaded preferences from database');
      } else {
        // No DB preferences - upload localStorage preferences to DB
        const localPrefs = this.getFromLocalStorage();
        this.cache = localPrefs;
        await this.syncToDatabase();
        console.log('[UserPreferences] Uploaded local preferences to database');
      }
    } catch (err) {
      console.error('[UserPreferences] Error during login sync:', err);
    }
  }

  /**
   * Get preferences from localStorage
   */
  private getFromLocalStorage(): DisplayPreferences {
    return {
      userMode: StorageService.get<'simple' | 'expert'>(LOCAL_KEYS.userMode, DEFAULT_PREFERENCES.userMode) || DEFAULT_PREFERENCES.userMode,
      showBTCComparison: StorageService.get<boolean>(LOCAL_KEYS.showBTCComparison, DEFAULT_PREFERENCES.showBTCComparison) || DEFAULT_PREFERENCES.showBTCComparison,
    };
  }

  /**
   * Sync preferences to localStorage
   */
  private syncToLocalStorage(prefs: DisplayPreferences): void {
    StorageService.set(LOCAL_KEYS.userMode, prefs.userMode);
    StorageService.set(LOCAL_KEYS.showBTCComparison, prefs.showBTCComparison);
  }

  /**
   * Sync preferences to database
   */
  private async syncToDatabase(): Promise<boolean> {
    if (!this.userId || !this.cache || this.syncInProgress) {
      return false;
    }

    this.syncInProgress = true;

    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: this.userId,
          display_preferences: this.cache,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id',
        });

      if (error) {
        console.error('[UserPreferences] Error syncing to database:', error);
        return false;
      }

      console.log('[UserPreferences] Synced to database:', this.cache);
      return true;
    } catch (err) {
      console.error('[UserPreferences] Error syncing to database:', err);
      return false;
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Clear cache (useful on logout)
   */
  clearCache(): void {
    this.cache = null;
    this.userId = null;
  }
}

export const userPreferencesService = new UserPreferencesService();
